/**
 * Schematic element action hooks for document state management
 *
 * These hooks dispatch document actions directly to the Zustand store via DocumentStore,
 * bypassing Redux. The DocumentStore handles:
 * 1. Injecting currentUser into actions (previously done by Redux currentUserMiddleware)
 * 2. Forwarding actions to the Zustand store's reduce() method
 *
 * The Zustand store's reduce() method notifies action listeners (ActionRecordService,
 * DocumentSaveService, PatchHandlerService) so middleware functionality is preserved.
 */

import {IElementData, IPartVersionData, VertexUpdate} from "@buildwithflux/models";
import {useCallback} from "react";

import {useFluxServices} from "injection/hooks";

import type {DocumentStore} from "../document";

/**
 * Hook to get the updateElementsSchematicPosition action
 *
 * Dispatches directly to Zustand via DocumentStore. The action listener system
 * ensures services (ActionRecordService, DocumentSaveService, PatchHandlerService)
 * receive the action for undo/redo and save functionality.
 *
 * @returns A function to update schematic element positions
 */
export function useUpdateElementsSchematicPosition(): (
    elements: IElementData[],
    wiringUpdates?: {vertexUpdates?: VertexUpdate[]},
) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (elements: IElementData[], wiringUpdates?: {vertexUpdates?: VertexUpdate[]}) => {
            documentStore.getState().updateElementsSchematicPosition(elements, wiringUpdates?.vertexUpdates);
        },
        [documentStore],
    );
}

/**
 * Hook to get the flipElements action
 *
 * Dispatches directly to Zustand via DocumentStore. The action listener system
 * ensures services (ActionRecordService, DocumentSaveService, PatchHandlerService)
 * receive the action for undo/redo and save functionality.
 *
 * @returns A function to flip schematic elements
 */
export function useFlipElements(): (elements: IElementData[], routeVertexUpdates?: VertexUpdate[]) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (elements: IElementData[], routeVertexUpdates?: VertexUpdate[]) => {
            documentStore.getState().flipElements(elements, routeVertexUpdates);
        },
        [documentStore],
    );
}

/**
 * Hook to get the replaceElementPartVersionDataCache action
 *
 * Dispatches directly to Zustand via DocumentStore. The action listener system
 * ensures services (ActionRecordService, DocumentSaveService, PatchHandlerService)
 * receive the action for undo/redo and save functionality.
 *
 * @returns A function to replace element part version data cache
 */
export function useReplaceElementPartVersionDataCache(): (elements: IElementData[]) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (elements: IElementData[]) => {
            documentStore.getState().replaceElementPartVersionDataCache(elements);
        },
        [documentStore],
    );
}

/**
 * Arguments for createLocalPart action
 */
interface CreateLocalPartArgs {
    element: IElementData;
    partVersion: IPartVersionData;
    newElement: IElementData;
}

/**
 * Hook to get the createLocalPart action
 *
 * Note: This action is only intended for dev use!
 *
 * Dispatches directly to Zustand via DocumentStore. The action listener system
 * ensures services (ActionRecordService, DocumentSaveService, PatchHandlerService)
 * receive the action for undo/redo and save functionality.
 *
 * @returns A function to create a local part from a selected element
 */
export function useCreateLocalPart(): (args: CreateLocalPartArgs) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (args: CreateLocalPartArgs) => {
            documentStore.getState().createLocalPart(args);
        },
        [documentStore],
    );
}

/**
 * Arguments for convertSubjectsToNewPart action
 */
interface ConvertSubjectsToNewPartArgs {
    elements: IElementData[];
    partVersion: IPartVersionData;
    newElement: IElementData;
    vertexUpdates?: VertexUpdate[];
    routeUpdates?: [];
}

/**
 * Hook to get the convertSubjectsToNewPart action
 *
 * Dispatches directly to Zustand via DocumentStore. The action listener system
 * ensures services (ActionRecordService, DocumentSaveService, PatchHandlerService)
 * receive the action for undo/redo and save functionality.
 *
 * @returns A function to convert subjects to a new part
 */
export function useConvertSubjectsToNewPart(): (args: ConvertSubjectsToNewPartArgs) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (args: ConvertSubjectsToNewPartArgs) => {
            documentStore.getState().convertSubjectsToNewPart(args);
        },
        [documentStore],
    );
}

/**
 * Hook to get the setHeadPartVersionDataCache action
 *
 * Dispatches directly to Zustand via DocumentStore. This action updates the
 * head part version data cache when receiving live updates from part drafts.
 *
 * NOTE: This action does not create an action record (shouldGenerateActionRecord: false)
 * because it can run when anybody views the doc.
 *
 * @returns A function to set the head part version data cache
 */
export function useSetHeadPartVersionDataCache(): (partUid: string, partVersionData: IPartVersionData) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (partUid: string, partVersionData: IPartVersionData) => {
            documentStore.getState().setHeadPartVersionDataCache(partUid, partVersionData);
        },
        [documentStore],
    );
}

/**
 * Hook to get the deleteSubjects action
 *
 * Dispatches directly to Zustand via DocumentStore. The action listener system
 * ensures services (ActionRecordService, DocumentSaveService, PatchHandlerService)
 * receive the action for undo/redo and save functionality.
 *
 * @returns A function to delete subjects (elements, routes, etc.)
 */
export function useDeleteSubjects(): (
    subjectUids: string[],
    vertexUpdates: VertexUpdate[],
    routeUpdates: [],
    uidsToDeselect: string[],
) => void {
    const {documentStore} = useFluxServices();

    return useCallback(
        (subjectUids: string[], vertexUpdates: VertexUpdate[], _routeUpdates: [], uidsToDeselect: string[]) => {
            documentStore.getState().deleteSubjectsAction(subjectUids, vertexUpdates, uidsToDeselect);
        },
        [documentStore],
    );
}

/**
 * Factory function to create schematic element actions for non-React contexts
 *
 * Use this in helper classes, services, or other non-React modules that need
 * to dispatch schematic element actions.
 *
 * @param documentStore - The document dispatcher from FluxServices
 * @returns An object with schematic element action methods
 */
export function createSchematicElementActions(documentStore: DocumentStore) {
    return {
        updateElementsSchematicPosition: (
            elements: IElementData[],
            wiringUpdates?: {vertexUpdates?: VertexUpdate[]},
        ) => {
            documentStore.getState().updateElementsSchematicPosition(elements, wiringUpdates?.vertexUpdates);
        },
        flipElements: (elements: IElementData[], routeVertexUpdates?: VertexUpdate[]) => {
            documentStore.getState().flipElements(elements, routeVertexUpdates);
        },
        replaceElementPartVersionDataCache: (elements: IElementData[]) => {
            documentStore.getState().replaceElementPartVersionDataCache(elements);
        },
        createLocalPart: (args: CreateLocalPartArgs) => {
            documentStore.getState().createLocalPart(args);
        },
        convertSubjectsToNewPart: (args: ConvertSubjectsToNewPartArgs) => {
            documentStore.getState().convertSubjectsToNewPart(args);
        },
        setHeadPartVersionDataCache: (partUid: string, partVersionData: IPartVersionData) => {
            documentStore.getState().setHeadPartVersionDataCache(partUid, partVersionData);
        },
        deleteSubjects: (
            subjectUids: string[],
            vertexUpdates: VertexUpdate[],
            _routeUpdates: [],
            uidsToDeselect: string[],
        ) => {
            documentStore.getState().deleteSubjectsAction(subjectUids, vertexUpdates, uidsToDeselect);
        },
    };
}
